/**
 * ご意見箱（fragments/feedback-box.html）の送信・文字数カウンタ・エラー表示（data 属性駆動）。
 *
 * <p>curriculum-feedback 05_ui.md §2.6。開閉は既存の accordion.ts に委ねる（本モジュールは関与しない）。
 * `<form>` を使わず、送信は必ず fetch 経由にする（Enter キーによる誤送信を避けるため）。
 */

interface FeedbackApiResponse {
  ok: boolean;
  message: string;
  field: string | null;
}

const GENERIC_ERROR_MESSAGE = "送信に失敗しました。時間をおいて再度お試しください。";

function csrfHeader(): Record<string, string> {
  const token = document.querySelector<HTMLMetaElement>('meta[name="_csrf"]')?.content ?? "";
  const header = document.querySelector<HTMLMetaElement>('meta[name="_csrf-header"]')?.content ?? "";
  return token !== "" && header !== "" ? { [header]: token } : {};
}

/**
 * 文字数カウンタを更新する。上限（max）は要素の maxlength 属性から読む（TS 側に定数を持たない。05_ui.md §2.6）。
 */
function updateCounter(input: HTMLInputElement | HTMLTextAreaElement, counter: HTMLElement | null): void {
  if (counter === null) return;
  const max = Number(input.getAttribute("maxlength"));
  if (!Number.isFinite(max)) return;

  const length = input.value.length;
  counter.textContent = `${length}/${max}`;
  counter.classList.toggle("is-over", length === max);
}

interface FeedbackElements {
  root: HTMLElement;
  curriculumIdInput: HTMLInputElement;
  titleInput: HTMLInputElement;
  contentInput: HTMLTextAreaElement;
  errorEl: HTMLElement;
  submitBtn: HTMLButtonElement;
  titleCounter: HTMLElement | null;
  contentCounter: HTMLElement | null;
  head: HTMLElement | null;
}

function collectElements(root: HTMLElement): FeedbackElements | null {
  const curriculumIdInput = root.querySelector<HTMLInputElement>("[data-lms-feedback-curriculum-id]");
  const titleInput = root.querySelector<HTMLInputElement>("[data-lms-feedback-title]");
  const contentInput = root.querySelector<HTMLTextAreaElement>("[data-lms-feedback-content]");
  const errorEl = root.querySelector<HTMLElement>("[data-lms-feedback-error]");
  const submitBtn = root.querySelector<HTMLButtonElement>("[data-lms-feedback-submit]");
  if (!curriculumIdInput || !titleInput || !contentInput || !errorEl || !submitBtn) {
    return null;
  }
  return {
    root,
    curriculumIdInput,
    titleInput,
    contentInput,
    errorEl,
    submitBtn,
    titleCounter: root.querySelector<HTMLElement>('[data-lms-feedback-counter="title"]'),
    contentCounter: root.querySelector<HTMLElement>('[data-lms-feedback-counter="content"]'),
    head: root.querySelector<HTMLElement>(".lms-fb__head"),
  };
}

function showError(errorEl: HTMLElement, message: string | null): void {
  if (message === null) {
    errorEl.hidden = true;
    errorEl.textContent = "";
    return;
  }
  errorEl.textContent = message;
  errorEl.hidden = false;
}

function markSubmitted(elements: FeedbackElements): void {
  elements.root.dataset["lmsFeedbackSubmitted"] = "true";
  if (elements.head !== null && elements.head.querySelector("[data-lms-feedback-badge]") === null) {
    const badge = document.createElement("span");
    badge.className = "lms-badge lms-badge--blue";
    badge.setAttribute("data-lms-feedback-badge", "");
    badge.textContent = "送信済み";
    elements.head.appendChild(badge);
  }
}

async function handleSubmit(elements: FeedbackElements): Promise<void> {
  const { contentInput, titleInput, curriculumIdInput, errorEl, submitBtn } = elements;

  const trimmedContent = contentInput.value.trim();
  if (trimmedContent === "") {
    showError(errorEl, "内容を記入してください");
    contentInput.classList.add("is-invalid");
    contentInput.focus();
    return; // 通信せずに終了する（spec.md「送信されない」）
  }

  showError(errorEl, null);
  contentInput.classList.remove("is-invalid");
  titleInput.classList.remove("is-invalid");

  submitBtn.disabled = true;
  const originalLabel = submitBtn.textContent;
  submitBtn.textContent = "送信中…";

  try {
    const response = await fetch("/api/feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json;charset=UTF-8", ...csrfHeader() },
      credentials: "same-origin",
      body: JSON.stringify({
        curriculumId: curriculumIdInput.value,
        title: titleInput.value,
        content: contentInput.value,
      }),
    });

    if (response.status === 401) {
      showError(errorEl, "セッションの有効期限が切れました。再度ログインしてください。");
    } else if (response.status === 403) {
      showError(errorEl, "送信に失敗しました。ページを再読み込みしてください。");
    } else if (response.status === 400) {
      const body = (await response.json()) as FeedbackApiResponse;
      showError(errorEl, body.message);
      if (body.field === "content") {
        contentInput.classList.add("is-invalid");
      } else if (body.field === "title") {
        titleInput.classList.add("is-invalid");
      }
    } else if (response.ok) {
      const body = (await response.json()) as FeedbackApiResponse;
      if (body.ok) {
        window.lmsToast("送信しました", "success");
        markSubmitted(elements);
      } else {
        showError(errorEl, GENERIC_ERROR_MESSAGE);
      }
    } else {
      showError(errorEl, GENERIC_ERROR_MESSAGE);
    }
  } catch {
    showError(errorEl, "送信に失敗しました。通信環境を確認してください。");
  } finally {
    submitBtn.disabled = false;
    submitBtn.textContent = originalLabel;
  }
}

export function initFeedbackBox(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLElement>("[data-lms-feedback]").forEach((root) => {
    if (root.dataset["lmsFeedbackReady"] === "1") return;
    root.dataset["lmsFeedbackReady"] = "1";

    const elements = collectElements(root);
    if (elements === null) return;

    updateCounter(elements.titleInput, elements.titleCounter);
    updateCounter(elements.contentInput, elements.contentCounter);

    elements.titleInput.addEventListener("input", () => {
      updateCounter(elements.titleInput, elements.titleCounter);
    });
    elements.contentInput.addEventListener("input", () => {
      updateCounter(elements.contentInput, elements.contentCounter);
      if (elements.contentInput.value.trim() !== "") {
        showError(elements.errorEl, null);
      }
    });

    elements.submitBtn.addEventListener("click", () => {
      void handleSubmit(elements);
    });
  });
}
